import { isArray } from 'lodash';

import at from '../constants/ActionTypes/MeshBot';
import actions from '../actions/MesheneActions';
import { apiGetMeshScenes } from '../api/apiScenes';
import { pollUntil } from './pollUntil';
import { buildListSyncCondition } from './buildListSyncCondition';

/**
 * Thunk that polls the cloud MeshBot list until the expected change (create/update/delete)
 * appears in the response, or until the maximum number of retries is exhausted.
 *
 * On success (change detected):
 *   - Updates the MeshBot list in Redux via `UPDATE_MESH_LIST_SCENES` and
 *     `GET_CLOUD_MESHBOT_LIST.success`.
 *
 * On timeout (change never observed):
 *   - Dispatches `onSyncTimeout` thunk to manually apply the expected change.
 *
 * Always turns off the line loading indicator when done.
 *
 * @param {string} uuid - UUID of the MeshBot being created, updated, or deleted.
 * @param {Object} [options]
 * @param {string} [options.operation] - One of {@link SYNC_OPERATION} values.
 * @param {Object|null} [options.previousItem=null] - Snapshot of the MeshBot before the operation
 *   (required for UPDATE — used to detect when `last_modified` has changed).
 * @param {number} [options.maxRetries=8] - Maximum number of polling attempts.
 * @param {Function} onSyncTimeout - Redux thunk dispatched when polling times out without
 *   observing the expected change. Typically created via {@link buildSyncTimeoutFallback}.
 * @returns {Function} Redux thunk `(dispatch) => Promise<void>`.
 *
 * @example
 * dispatch(pollUntilMeshBotSynced(
 *     uuid,
 *     { operation: SYNC_OPERATION.CREATE },
 *     buildSyncTimeoutFallback(SYNC_OPERATION.CREATE, uuid, params, null),
 * ));
 */
export const pollUntilMeshBotSynced =
    (uuid, { operation, previousItem = null, maxRetries = 8 } = {}, onSyncTimeout) =>
    async (dispatch) => {
        const listCondition = buildListSyncCondition(operation, uuid, previousItem);

        const { result, found } = await pollUntil(
            apiGetMeshScenes,
            (res) => isArray(res?.data?.data?.list) && listCondition(res.data.data.list),
            { maxRetries, minDelay: 2000, maxDelay: 5000 },
        );

        if (found) {
            const { list } = result.data.data;
            dispatch(actions.updateMeshScenes(list));
            dispatch({ type: at.GET_CLOUD_MESHBOT_LIST.success, data: list });
        } else {
            dispatch(onSyncTimeout);
        }
    };
